import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import api from '@/api';
// images
import LinkedInLoginIcon from '@/public/images/linked-in-login-icon.svg';
import LinkedInIcon from '@/public/images/LinkedIN.svg';
import {
  LinkedinIcon,
  LinkedinFillIcon,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';

// styles
import styles from './styles.module.scss';
// types
import { ILoginType } from '@/components/landing-page-templates/ConfigLandingPage/types';
import { IAccountRegistrationEnum } from '@/models/account/types';
// configurations
import { LINKEDIN_CLIENT_ID } from '@/config';

const LinkedInLoginButton = ({
  updateLoginMethod,
  updateLastLoginMethod,
  existingLoginMethod,
  signInButton = false,
  isAttendeeRegistration,
  setCheckRegistrationExists,
  organizationId,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams<{ eventId: string }>();

  const [linkedinErrorMessage, setLinkedinErrorMessage] = useState(null);

  const redirectUri: string = `${window.location.origin}/a/linkedin`;

  // Handle a successful LinkedIn Login Button response
  const handleSuccess = response =>
    api.account
      .linkedInLogin_landingPage(
        response.code,
        eventId,
        organizationId,
        redirectUri,
        isAttendeeRegistration,
      )
      .then(({ data }) => {
        dispatch({ type: 'account/setAccount' });
        dispatch({ type: 'account/setUserData', payload: { data } });
        dispatch({
          type: 'account/setRegistrationStep',
          payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
        });
      })
      .catch(e => {
        console.error(e);
        console.error(
          `LinkedIn login failed. [Error: ${linkedinErrorMessage}]`,
        );
        // NOTE - checking for the 500 error with the custom message
        //  "INVITE_ONLY_NO_ACCESS" for this case. Was trying to return 403
        //  "Forbidden" but it was not working on the production environment.
        if (
          e.response?.status === 500 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e.response?.status === 401 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e?.response?.status === 406 &&
          e?.response?.data.message === 'NOT_ACCEPTABLE'
        ) {
          setCheckRegistrationExists(false);
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: e?.response?.data.description },
          });
        }
        // Event Level login
        // 406 "Not Acceptable" error means they are using the wrong auth mode/type
        else if (
          e.response?.status === 406 &&
          updateLoginMethod &&
          updateLastLoginMethod
        ) {
          // Set the Login method to LinkedIn and set the last login method from the API response (406 means the auth methods don't match)
          updateLoginMethod(ILoginType.LINKEDIN);
          updateLastLoginMethod(e.response.data);
        }
        // App level login
        else if (e.response?.status === 406) {
          dispatch({
            type: 'global/addDangerToast',
            payload: {
              description: `You need to sign in with ${e.response.data}`,
            },
          });
        } else {
          // Some other error occurred while logging in with linkedin
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: 'Unable to sign in with LinkedIn.' },
          });
        }
      });

  const handleFailure = error => {
    setLinkedinErrorMessage(error.errorMessage);
    console.error(error);
  };

  const linkedInImageWithText = (
    <div>
      {existingLoginMethod ? (
        <div className={styles.buttonTextContainer}>
          <img
            src={LinkedInIcon}
            alt="LinkedIn"
            className={styles.socialButtonIconWithText}
          />
          <div
            data-testid="sign-in-with-linkedin"
            className={styles.linkedInButtonText}
          >
            {signInButton ? `Sign in with LinkedIn` : `Continue with LinkedIn`}
          </div>
        </div>
      ) : (
        <div>
          <LinkedinFillIcon color="#0073B1" />
        </div>
      )}
    </div>
  );

  // NOTE- copied this style from .newSocialButton CSS class
  const loginButtonStyle = {
    border: '1px solid #BCCBDC',
    width: '103px',
    height: '36px',
    textAlign: 'center',
    margin: '0px 4px',
    padding: '8px',
    borderRadius: '5px',
    cursor: 'pointer',
    background: '#FFFFFF',
  };

  const existingLoginButtonStyle = {
    width: '356px',
    minWidth: '356px',
    height: '71px',
    marginRight: '6px',
    marginBottom: '25px',
    background: '#0077B5',
    border: '1px solid #0077B5',
    borderRadius: '5px',
    cursor: 'pointer',
    color: '#ffffff',
  };

  return (
    <div>
      <LinkedIn
        clientId={LINKEDIN_CLIENT_ID}
        onFailure={handleFailure}
        onSuccess={handleSuccess}
        scope="r_liteprofile,r_emailaddress"
        // e.g. 'https://localhost:8080/a/linkedin'
        redirectUri={redirectUri}
        // NOTE - this prop is required to be an object (not a scss module style)
        style={
          existingLoginMethod ? existingLoginButtonStyle : loginButtonStyle
        }
      >
        {linkedInImageWithText}
      </LinkedIn>
    </div>
  );
};

export default LinkedInLoginButton;
